import {Schema} from "effect";

// ── Schema ──────────────────────────────────────────────────────────────

/**
 * An organization UID is a string that starts with "org_" followed by an arbitrary suffix.
 *
 * Effect Schema equivalent of the legacy zod `OrganizationUid` declared in
 * `@buildwithflux/identifiers`. Acceptance semantics are identical (see
 * runtime parity tests in `OrganizationUid.test.ts`).
 */
const OrganizationUidSchema = Schema.TemplateLiteral("org_", Schema.String).pipe(Schema.brand("OrganizationUid"));

export type OrganizationUid = typeof OrganizationUidSchema.Type;

// ── Refinement predicate ───────────────────────────────────────────────

/**
 * Type guard — decides whether a string is a branded OrganizationUid.
 */
export const isOrganizationUid = Schema.is(OrganizationUidSchema);

// ── Constructors ────────────────────────────────────────────────────────

/**
 * Mint a new OrganizationUid by generating a fresh UUID v4 suffix.
 *
 * Matches the legacy zod `createNewOrganizationUid()` 1:1 — no-arg, internally
 * generates the UUID — so M1.3 codemods are pure symbol swaps.
 */
export function createNewOrganizationUid(): OrganizationUid {
    return `org_${crypto.randomUUID()}` as OrganizationUid;
}

// ── Companion object ────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const OrganizationUid = {
    schema: OrganizationUidSchema,
    isOrganizationUid,
    createNewOrganizationUid,
} as const;
